/**
 * <voucher-list> — the codes an event carries, and the way into the form that
 * mints one. A routed destination in the console's own nested `event-console`
 * area, so the chosen destination survives a reload or a deeplink exactly
 * like every other one (dashboard.view.ts's header for why).
 *
 * It reads the event from `consoleEventId` rather than taking one as a
 * property: the area constructs a routed component with no bindings. That is
 * also what makes the form beneath it able to refuse an event picker — the
 * operator is already inside the event that owns every code on this screen.
 *
 * A CODE IS A PRINTED THING WITH AN ALLOWANCE, so each one is a card and not a
 * row: <voucher-card>'s own header carries what that card states and why it
 * states it in that order.
 *
 * NOTHING HERE MOVES WHEN A CODE STOPS. The order is the issue order, and a
 * code that reaches its limit or its expiry keeps its place rather than
 * sinking to a stopped section — its own card says so instead. The placeholder
 * a first read lands in is the card's own geometry in the card's own grid, so
 * the codes arrive where their placeholders stood.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { html, type TemplateResult } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { consoleEventId } from "../events/console/console.state.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import { canManageVouchers, vouchersPageView } from "./vouchers.state.ts";
import "./voucher-card.view.ts";
import "./voucher-form.view.ts";

/** Three cards' worth of placeholder — the band a first read lands in, at the
 *  card's own line stack so nothing reflows when the codes arrive. */
const SKELETON_SLOTS = [0, 1, 2];

@schmancyElement("voucher-list")
export default class VoucherListView extends LocalizeMixin(SchmancyElement) {
	private readonly mint = (): void => {
		const eventId = consoleEventId.value;
		if (!eventId) return;
		show(
			() => html`<voucher-form .eventId=${eventId} .code=${""}></voucher-form>`,
			{ ariaLabel: this.t("admin.vouchers.list.new") },
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};

	override render(): TemplateResult {
		const view = vouchersPageView.get();

		return html`
			<div ${overflowWithin()} class="flex flex-col gap-4 p-4 md:p-6">
				<div class="flex flex-wrap items-center gap-3">
					<div class="flex min-w-0 flex-col">
						<schmancy-typography type="title" token="md">
							${this.t("admin.vouchers.list.title")}
						</schmancy-typography>
						<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
							${this.t("admin.vouchers.list.intro")}
						</schmancy-typography>
					</div>
					${when(
						canManageVouchers.get(),
						() => html`
							<schmancy-button class="ml-auto" variant="filled" @click=${this.mint}>
								<schmancy-icon slot="prefix" aria-hidden="true">add</schmancy-icon>
								${this.t("admin.vouchers.list.new")}
							</schmancy-button>
						`,
					)}
				</div>

				<!-- One region for every placeholder below it, naming the work rather
				     than the fact of waiting, and swapping to the outcome so the
				     announcement has an audible end. -->
				<schmancy-visually-hidden role="status" aria-live="polite">
					${
						view.status === "loading"
							? this.t("admin.vouchers.list.loading")
							: view.status === "ready"
								? this.t("admin.vouchers.list.loaded", {
										count: view.cards.length,
									})
								: this.t("admin.vouchers.list.loadFailed")
					}
				</schmancy-visually-hidden>

				${
					view.status === "loading"
						? html`
						<div
							class="grid grid-cols-[repeat(auto-fill,minmax(22rem,1fr))] items-start gap-3"
							aria-hidden="true"
						>
							${repeat(
								SKELETON_SLOTS,
								(slot) => slot,
								() => html`
									<div class="flex items-start gap-4 rounded-2xl bg-surface-containerLow p-4">
										<div class="flex min-w-0 flex-1 flex-col gap-1">
											<schmancy-skeleton shape="text" class="h-7 w-32"></schmancy-skeleton>
											<schmancy-skeleton shape="text" class="h-5 w-48"></schmancy-skeleton>
											<schmancy-skeleton shape="text" class="h-4 w-40"></schmancy-skeleton>
											<schmancy-skeleton shape="text" class="h-4 w-28"></schmancy-skeleton>
										</div>
										<schmancy-skeleton shape="circle" class="size-14 shrink-0"></schmancy-skeleton>
									</div>
								`,
							)}
						</div>
					`
						: view.status !== "ready"
							? html`
							<schmancy-typography type="body" token="md" class="text-error-default">
								${this.t("admin.vouchers.list.loadFailed")}
							</schmancy-typography>
						`
							: view.cards.length === 0
								? html`
								<schmancy-typography type="body" token="md" class="text-surface-onVariant">
									${this.t("admin.vouchers.list.empty")}
								</schmancy-typography>
							`
								: html`
								<div class="grid grid-cols-[repeat(auto-fill,minmax(22rem,1fr))] items-start gap-3">
									${repeat(
										view.cards,
										(card) => card.code,
										(card) => html`<voucher-card .card=${card}></voucher-card>`,
									)}
								</div>
							`
				}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"voucher-list": VoucherListView;
	}
}
